import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Settings, RefreshCw, Trash2, Check, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DataSource {
  id: string;
  name: string;
  type: 'health_system' | 'wearable' | 'app' | 'manual';
  status: 'connected' | 'disconnected' | 'pending';
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  lastSync?: string;
  enabled: boolean;
}

interface DataSourceSettingsDialogProps {
  source: DataSource;
  onReconnect?: (id: string) => void;
  onDisconnect?: (id: string) => void;
  onRemove?: (id: string) => void;
}

export const DataSourceSettingsDialog = ({
  source,
  onReconnect,
  onDisconnect,
  onRemove,
}: DataSourceSettingsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'disconnected': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <Check className="h-4 w-4" />;
      case 'pending': return <X className="h-4 w-4" />;
      case 'disconnected': return <X className="h-4 w-4" />;
      default: return <X className="h-4 w-4" />;
    }
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      // Simulate reconnection
      await new Promise(resolve => setTimeout(resolve, 1500));
      onReconnect?.(source.id);
      setOpen(false);
    } catch (error) {
      console.error('Error reconnecting:', error);
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      // Simulate disconnection
      await new Promise(resolve => setTimeout(resolve, 1000));
      onDisconnect?.(source.id);
      setOpen(false);
    } catch (error) {
      console.error('Error disconnecting:', error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleRemove = () => {
    setShowRemoveDialog(true);
  };

  const confirmRemove = () => {
    onRemove?.(source.id);
    setShowRemoveDialog(false);
    setOpen(false);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'health_system': return 'Health System';
      case 'wearable': return 'Wearable Device';
      case 'app': return 'Mobile App';
      case 'manual': return 'Manual Entry';
      default: return type;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <source.icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div>{source.name}</div>
                <div className="text-sm font-normal text-muted-foreground">
                  {getTypeLabel(source.type)}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Connection Status */}
            <div>
              <h4 className="text-sm font-medium mb-2">Connection Status</h4>
              <div className="flex items-center space-x-2">
                <Badge className={`flex items-center space-x-1 ${getStatusColor(source.status)}`}>
                  {getStatusIcon(source.status)}
                  <span className="capitalize">{source.status}</span>
                </Badge>
                {source.lastSync && (
                  <span className="text-sm text-muted-foreground">
                    Last sync: {source.lastSync}
                  </span>
                )}
              </div>
            </div>

            <Separator />

            {/* Description */}
            <div>
              <h4 className="text-sm font-medium mb-2">Description</h4>
              <p className="text-sm text-muted-foreground">{source.description}</p>
            </div>

            <Separator />

            {/* Actions */}
            <div className="space-y-2">
              {source.status === 'disconnected' && (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={handleReconnect}
                  disabled={isReconnecting}
                >
                  {isReconnecting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Reconnecting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reconnect
                    </>
                  )}
                </Button>
              )}

              {source.status === 'connected' && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-2" />
                      Disconnect
                    </>
                  )}
                </Button>
              )}

              <Button
                variant="destructive"
                className="w-full"
                onClick={handleRemove}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove Source
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Data Source?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{source.name}</strong>? 
              This will disconnect the source and remove its data from your health profile. 
              You can reconnect it later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
